// The Explore card: the site analytics report's only row-level surface.
//
// Everything the card shows comes from payloads the page pushes over window events
// (site-analytics-explore for a grain, site-analytics-row for an expanded row). The card is
// wire:ignore'd, so no Livewire property can repaint it, and every figure arrives already
// formatted by PHP. An expanded row grouped into weeks or months is grouped by the server too, at
// the interval its chart's switch picked, so nothing here formats a number or invents one the
// payload did not carry.

export const STATUS_IDLE = "idle";
export const STATUS_LOADING = "loading";
export const STATUS_DATA = "data";
export const STATUS_EMPTY = "empty";
export const STATUS_FAILED = "failed";

// ReportDefinition::GRAIN_ORDER_DEFAULT. Every grain offers it; the others arrive in the payload.
export const ORDER_DEFAULT = "default";

// ReportDefinition::GRAIN_ORDER_OPPORTUNITY: the one order that reads every row whatever the row set
// (D10), so the card keys its answers as every row and offers no row control under it.
export const ORDER_OPPORTUNITY = "opportunity";

// ReportDefinition::GRAIN_ORDER_COLUMN_PREFIX: a header's sort is `col:<measure>:<asc|desc>`, a value
// of the same order setting as the toggles, so ?order= and the toggles can never disagree with it.
export const COLUMN_ORDER_PREFIX = "col:";

export const SORT_DESCENDING = "desc";
export const SORT_ASCENDING = "asc";

// ReportDefinition::GRAIN_ROW_SET_ALL and GRAIN_ROW_SET_CLICKED.
export const ROW_SET_ALL = "all";
export const ROW_SET_CLICKED = "clicked";

// The card's name for "no segment" (the payload's null). Every other segment key is data: which
// ones a grain offers arrives with the card, from ReportDefinition, so this file never names one.
export const SEGMENT_ALL = "all";

// The card's name for "whatever columns the grain shows by default", so a reader who has chosen
// nothing is not filed apart from the answer the page already holds.
export const COLUMNS_DEFAULT = "";

// One grain read in one order, one segment, one column set and one row set is one answer: two orders,
// the branded queries beside all of them, two column sets or the clicked rows beside every row, each
// hold different rows or different cells, so each is requested, cached and failed on its own.
//
// The PAGE is deliberately not in the key. A second page ADDS its rows to the answer already held —
// keying it apart would file page 2 as its own table and show the reader rows 26-50 alone.
export function answerKey(grain, order, segment = SEGMENT_ALL, columns = COLUMNS_DEFAULT, rowSet = ROW_SET_ALL) {
    return `${grain}|${order}|${segment}|${columns}|${rowSet}`;
}

// A header sort's column and direction, or null for an order that is not one.
export function columnOrderParts(order) {
    if (typeof order !== "string" || !order.startsWith(COLUMN_ORDER_PREFIX)) {
        return null;
    }

    const [measure, direction] = order.slice(COLUMN_ORDER_PREFIX.length).split(":");

    return measure && (direction === SORT_ASCENDING || direction === SORT_DESCENDING) ? { measure, direction } : null;
}

// The order a header press asks for. A sort on the column the default order ranks by, largest
// first, IS the default order (the page answers it as `default`), so it is asked for by that name
// and lands on the answer the card already holds rather than a second copy of it.
export function columnOrder(measure, direction, firstColumn) {
    return measure === firstColumn && direction === SORT_DESCENDING ? ORDER_DEFAULT : `${COLUMN_ORDER_PREFIX}${measure}:${direction}`;
}

// How the table on screen is ranked, as a header shows it: the default order ranks by its first
// column, largest first; a header sort by its own column. Change and Biggest opportunity rank by a
// figure no header sorts, so no header reads as sorted under them.
export function sortOf(order, firstColumn) {
    if (order === ORDER_DEFAULT) {
        return firstColumn ? { measure: firstColumn, direction: SORT_DESCENDING } : null;
    }

    return columnOrderParts(order);
}

// Whether the answer can offer what was asked. An answer lists every order and row set it could have
// run, so one that does not list the asked value is the page falling back from it (a sorted column
// the reader then hid, clicks this site cannot read), while a late answer for a value the reader has
// since left still lists the one they are on now.
export function offers(list, value) {
    return Array.isArray(list) && list.some((item) => (item && typeof item === "object" ? item.key : item) === value);
}

// The columns an answer names in query.columns, as one token; null when the payload names none (an
// answer from before columns were chooseable), in which case the card files it under what it asked.
export function columnsOfPayload(payload) {
    if (!payload || !payload.query || !Array.isArray(payload.query.columns)) {
        return null;
    }

    return payload.query.columns.join(",");
}

// The segment an answer names in query.segment, or null when the payload names none (an answer from
// before segments existed), in which case the card files it under the segment it asked for.
export function segmentOfPayload(payload) {
    if (!payload || !payload.query || !Object.prototype.hasOwnProperty.call(payload.query, "segment")) {
        return null;
    }

    return payload.query.segment || SEGMENT_ALL;
}

// The row set an answer names in query.rowSet, or null when the payload names none.
export function rowSetOfPayload(payload) {
    return payload && payload.query && typeof payload.query.rowSet === "string" ? payload.query.rowSet : null;
}

// One opened row's rows lined up under the table's columns: each column's cell from the drill row
// where the drill grain carries that measure, and an empty cell where it does not (a query's page
// views: Google Analytics records no search), so a figure is never shown under another's header.
export function drillCells(columns, row) {
    const cells = new Map((row && row.cells ? row.cells : []).map((cell) => [cell.key, cell]));

    return columns.map((column) => {
        const cell = column.isChange ? undefined : cells.get(column.key);

        return cell ? { key: column.key, formatted: cell.formatted, isMissing: false } : { key: column.key, formatted: null, isMissing: true };
    });
}

// Page N of a grain folded into the pages already held: the new rows appended after the old ones,
// and every other field taken from the newer answer so `paging` (and with it the footer's total and
// whether more exist) follows the last page fetched.
//
// Rows already held are dropped rather than repeated: a page re-answered — a double press, a page
// retried after a refusal — must not double the table.
export function withMoreRows(held, next) {
    if (!next.hasData) {
        // Page N came back with nothing, which happens when rows shift under a reader between two
        // presses. Keep the table they are reading and stop offering more, rather than replacing
        // what they have with "no rows for this period".
        return { ...held, paging: { ...held.paging, hasMore: false } };
    }

    const seen = new Set(held.rows.map((row) => row.key));

    return { ...next, rows: [...held.rows, ...next.rows.filter((row) => !seen.has(row.key))] };
}

// The payload's columns with the change column placed straight after the column it compares.
// Which column that is comes from payload.comparison, so no measure is named here.
export function tableColumns(payload) {
    if (!payload || !payload.hasData) {
        return [];
    }

    const comparison = payload.comparison;
    const columns = [];

    payload.columns.forEach((column) => {
        columns.push({ ...column, isChange: false });

        if (comparison && column.key === comparison.measure) {
            columns.push({
                key: `${column.key}:change`,
                label: comparison.label,
                sourceLabel: column.sourceLabel,
                align: "right",
                isChange: true,
            });
        }
    });

    return columns;
}

// A row's cells in tableColumns() order. The (other) row's change is always null, so its change
// cell carries nothing to print.
export function tableCells(payload, row) {
    if (!row) {
        return [];
    }

    const comparison = payload && payload.hasData ? payload.comparison : null;
    const cells = [];

    row.cells.forEach((cell) => {
        cells.push({ ...cell, isChange: false });

        if (comparison && cell.key === comparison.measure) {
            cells.push({ key: `${cell.key}:change`, isChange: true, change: row.change || null });
        }
    });

    return cells;
}

// The reader's period, filters and search type as one comparable value. A grain payload splits
// the reader's filters into applied and notApplied, and so does every report query, so their union
// is the reader's filter set whichever payload it was read from.
export function contextOf(payloads) {
    const list = payloads.filter((payload) => payload && payload.query);

    if (list.length === 0) {
        return null;
    }

    const filters = new Set();
    const scopes = {};

    list.forEach((payload) => {
        // The OPERATOR is part of the tuple, and has to be: `contains seo` and `not_contains seo`
        // are complementary row sets over one dimension and one value, so a tuple of
        // [dimension, value] alone makes them compare EQUAL. A late answer for the operator the
        // reader just left would then be merged instead of discarded, and filed under the operator
        // they are now on, so the table would show the wrong one's rows with no error and no
        // spinner.
        [...(payload.query.filters || []), ...(payload.query.notApplied || [])].forEach((filter) => {
            filters.add(JSON.stringify([filter.dimension, filter.operator ?? 'equals', filter.value]));
        });
        Object.assign(scopes, payload.scopes || {});
    });

    return {
        start: list[0].period.start,
        end: list[0].period.end,
        filters: [...filters].sort(),
        scopes,
    };
}

// A GA-only grain binds no search type while a GSC payload does, so scopes compare only where both
// sides carry the parameter.
export function sameContext(a, b) {
    if (!a || !b) {
        return a === b;
    }

    if (a.start !== b.start || a.end !== b.end || JSON.stringify(a.filters) !== JSON.stringify(b.filters)) {
        return false;
    }

    return Object.keys(a.scopes).every((key) => !(key in b.scopes) || a.scopes[key] === b.scopes[key]);
}

// Which sources the page last said were connected, as one comparable value; null when the event
// carried no availability. A grain is read from the connected sources, so connecting one changes
// the answer to an unchanged question.
export function availabilityKeyOf(detail) {
    const availability = detail && detail.availability;

    if (!availability || typeof availability !== "object") {
        return null;
    }

    return JSON.stringify(Object.keys(availability).sort().map((source) => [source, availability[source]]));
}

// The dimension a grain's rows are keyed by, and whether the report offers it as a filter. The
// payload names it (query.grain has no dimension; payload.dimension does), so the card never maps
// a grain to a dimension itself and a grain that changes what it groups by cannot leave a Filter
// control setting the wrong chip.
export function filterDimensionOf(payload, filterDimensions) {
    const key = payload && payload.hasData && payload.dimension ? payload.dimension.key : null;

    return key && filterDimensions.includes(key) ? key : null;
}

// The operators under which a filter can BE one row: `contains seo` or a pattern matches many rows
// and is none of them, so a row never reads as pressed under one. `exact` is the case-insensitive
// whole-value match a row press applies to the Query filter (ROW_QUERY_FILTER_OPERATOR).
const WHOLE_VALUE_MATCHES = {
    equals: (value, key) => value === key,
    exact: (value, key) => value.toLowerCase() === key.toLowerCase(),
};

// Whether the answer the card is drawing was narrowed to exactly this row's value, so the control
// can say "this row IS the filter" rather than offering to apply it again. Read from query.filters,
// which is what reached BigQuery, not from the chip the page happens to show.
export function isFilteredTo(payload, dimensionKey, rowKey) {
    const applied = (payload && payload.query && payload.query.filters) || [];
    const match = applied.find((filter) => filter.dimension === dimensionKey);
    const isSame = match ? WHOLE_VALUE_MATCHES[match.operator ?? "equals"] : undefined;

    return isSame !== undefined && isSame(match.value, rowKey);
}

export function statusOfPayload(payload) {
    if (payload.error) {
        return STATUS_FAILED;
    }

    return payload.hasData ? STATUS_DATA : STATUS_EMPTY;
}

// The grain the card opens on. The host page may already have one selected (from ?grain=), and
// init() asks for whatever this returns, so starting on the first grain regardless would call
// loadGrain('pages') and reset the page's choice. A value the card has no tab for falls back to
// the first grain rather than leaving no tab selected.
export function startingGrain(config) {
    const known = config.grains.some((item) => item.key === config.grain);

    return known ? config.grain : config.grains[0].key;
}

export default (config) => ({
    id: config.id,
    grains: config.grains,
    strings: config.strings,
    // Where one page's own report lives, with the site already in the path. The row appends what
    // it is a row OF; the period and search type come off the payload the table was read with, so
    // the link always names the view the reader is looking at.
    pageReportUrl: config.pageReportUrl || '',

    // Named grainPayloads and rowAnswer, never payloads and rowPayloads. The open row's chart is
    // a nested SiteAnalyticsReport scope, and Alpine runs every method with `this` bound to the
    // merged scope stack, innermost first: an Explore method called from inside that subtree
    // would read the chart's own `payloads` and `rowPayloads` instead of the card's, and see an
    // open row with no answer.
    grain: startingGrain(config),
    // Which segments each grain offers THIS site (ReportDefinition::grainSegments(), minus any the
    // site cannot use), and each segment's URL flag: the page property it is written to and the
    // value it writes there (Analytics::segmentUrlFlags()). Both are fixed for the life of the page,
    // so they arrive with the card.
    grainSegments: config.grainSegments || {},
    segmentFlags: config.segmentFlags || {},
    // ReportDefinition::FILTER_DIMENSIONS. Fixed for the life of the page, so it arrives with the
    // card rather than being inferred from a payload.
    filterDimensions: config.filterDimensions || [],
    // The typed dimension and the operator a row press applies to it; see toggleRowFilter().
    textFilterDimension: config.textFilterDimension || null,
    rowQueryOperator: config.rowQueryOperator || null,
    // The row set each grain that offers one opens on (ReportDefinition::defaultGrainRowSet(): Queries
    // on the clicked rows, Pages on every row), and the figure the clicked set compares. A grain absent
    // here offers no row control.
    rowSetDefaults: config.rowSetDefaults || {},
    rowSetMeasure: config.rowSetMeasure || "clicks",
    // Which grain a row of each grain opens into (ReportDefinition::grainDrill()): a grain absent here
    // opens to its chart alone.
    drillGrains: config.drillGrains || {},
    // The direction each column's header sorts in on its first press, per grain and measure: best first
    // (ReportDefinition::firstSortDirection()).
    sortFirst: config.sortFirst || {},
    // grainPayloads, requested and requestFailed are keyed answerKey(grain, order, segment, columns). orders holds
    // the order each grain is read in, and has no entry for a grain until its first answer names
    // one: that first request sends no order, so the page runs the ?order= it was opened with.
    grainPayloads: {},
    requested: {},
    requestFailed: {},
    orders: {},
    // The segment each grain is read in, adopted from its first answer like orders; only grains that
    // offer one get an entry.
    segments: {},
    // The measures each grain shows, as a list in the grain's own declared order. No entry until the
    // reader chooses or an answer names one, so a grain's first request sends no columns and the page
    // runs the ?columns= (or the reader's saved choice) it was opened with, exactly as with the order.
    selected: {},
    // The measures each grain shows BY DEFAULT, from whichever answer first named them. Kept here
    // rather than read off the payloads because a filter change drops every payload, and the answer
    // key must not drift when it does: a reader sitting on the default set would otherwise ask under
    // one key and be answered under another, and spin on a table the browser already had.
    defaults: {},
    // Keyed by answerKey: whether a NEXT page is in flight, and whether the last one failed. Both are
    // about the Show-more button alone — a page that never arrives must not replace the rows on
    // screen with a skeleton or a Retry box, because those rows are still the right answer.
    morePending: {},
    moreFailed: {},
    context: null,
    availability: null,
    // Per grain, the row keys that get the "Draft an article" control, as a Set: the server decides
    // them per reader and per site (Analytics::draftableKeys()) and sends them beside each answer. A
    // Set, never an object keyed by the search, where a search spelled "constructor" would read an
    // inherited member as true. Neither the period nor the filters change the decision, so it
    // outlives a context change and only ever grows.
    draftable: {},
    // The row set each grain is read in, adopted from its first answer that offered a choice, like
    // orders: until then a grain's request sends none and the page runs the ?rows= it was opened with.
    rowSets: {},
    // Per grain, each row's topic as a Map (Analytics::rowTopics()), or null when the site has none to
    // show and the Topic column stays off. Sent beside every answer, so a new answer's rows add theirs.
    topics: {},
    // Per grain, the Hidden by Google line the latest answer carried (Analytics::hiddenByGoogleLine()),
    // or null for none. Only the Queries grain has one; a new question drops it with the rows.
    hiddenLines: {},

    openRow: null,
    rowAnswer: null,
    // The rows the open row lists under itself (loadRowDrill()): the answer for the open row as it
    // arrived, and whether its request failed. Apart from rowAnswer, which is the chart's, because the
    // two are asked for on two limiters and land on their own.
    drillAnswer: null,
    drillRequestFailed: false,
    rowRequestFailed: false,
    // The open row's own interval, as its chart's switch picked it, and the switch its latest answer
    // carried. The pick is the open row's alone: every row opens at the page's Interval, and none of
    // it reaches the URL.
    rowInterval: null,
    rowIntervalSwitch: null,
    // Whether the open row is being asked again by its switch with its chart still mounted, so a
    // failed ask has an answer on screen to take down rather than none.
    isRowReasking: false,

    init() {
        this.selectGrain(this.grain);
    },

    destroy() {
        this.closeRow();
    },

    // Livewire's $wire is only reachable from inside a component's Alpine scope; kept behind one
    // method each so the state machine above never names it.
    // The one place an answer's identity is spelled, so adding an axis to it does not mean finding
    // every call site again.
    keyOf(grain) {
        return answerKey(grain, this.orderOf(grain), this.segmentOf(grain), this.columnsTokenOf(grain), this.rowSetKeyOf(grain));
    },

    // page > 1 asks for the NEXT page of an answer already on screen: requested/requestFailed are
    // left alone (the table is not loading — only the Show-more button is) and morePending carries
    // the press instead, set by loadMore() before this runs so the button reads as busy in the same
    // frame the reader pressed it.
    requestGrain(grain, page = 1) {
        const key = this.keyOf(grain);

        if (page === 1) {
            this.requested[key] = true;
            delete this.requestFailed[key];
        }

        // Only what an answer has settled is named: before that, the page runs the ?order=, the segment
        // flag and ?columns= it was opened with, or the column set this reader saved. Positional, so
        // the trailing nulls of a request that settled nothing are trimmed and loadGrain('pages')
        // stays exactly the call it always was. A null order keeps the page's order while naming the
        // segment, and the segment travels as its own key, false for all rows, rather than a yes, so
        // the page never has to guess which of a grain's segments a bare yes meant.
        const args = [
            grain,
            grain in this.orders ? this.orders[grain] : null,
            grain in this.segments ? (this.segments[grain] === SEGMENT_ALL ? false : this.segments[grain]) : null,
            page > 1 ? page : null,
            grain in this.selected ? [...this.selected[grain]] : null,
            grain in this.rowSets ? this.rowSets[grain] : null,
        ];

        while (args.length > 1 && args[args.length - 1] === null) {
            args.pop();
        }

        const call = this.$wire.loadGrain(...args);

        if (call && typeof call.catch === "function") {
            call.catch(() => {
                if (page > 1) {
                    delete this.morePending[key];
                    this.moreFailed[key] = true;

                    return;
                }

                if (!this.grainPayloads[key]) {
                    this.requestFailed[key] = true;
                }
            });
        }
    },

    requestRow(grain, value) {
        this.rowRequestFailed = false;

        // The interval only once the row's switch picked one, so a row at the page's Interval is
        // asked exactly as it always was.
        const args = [grain, value];

        if (this.rowInterval !== null) {
            args.push(this.rowInterval);
        }

        const call = this.$wire.expandRow(...args);

        if (call && typeof call.catch === "function") {
            call.catch(() => {
                if (this.openRow && this.openRow.grain === grain && this.openRow.key === value && (!this.rowAnswer || this.isRowReasking)) {
                    // A re-ask keeps the previous answer mounted, so its failure takes that answer
                    // down too: the chart would otherwise wait on loading with nothing coming.
                    this.rowAnswer = null;
                    this.isRowReasking = false;
                    this.rowRequestFailed = true;
                }
            });
        }
    },

    // The rows the open row lists under itself, on the Explore limiter (loadRowDrill()). A query row
    // sends its own impressions, as the table printed them, for its top page (TopPage::pick() needs
    // the query's own count, which the table already has); a row that shows none sends null, and the
    // page says why no page is marked.
    requestDrill(grain, row) {
        if (!(grain in this.drillGrains) || !row) {
            return;
        }

        this.drillRequestFailed = false;

        const impressions = (row.cells || []).find((cell) => cell.key === "impressions");
        const args = [grain, row.key];

        if (impressions && Number.isInteger(impressions.value)) {
            args.push(impressions.value);
        }

        const call = this.$wire.loadRowDrill(...args);

        if (call && typeof call.catch === "function") {
            call.catch(() => {
                if (this.openRow && this.openRow.grain === grain && this.openRow.key === row.key && !this.drillAnswer) {
                    this.drillRequestFailed = true;
                }
            });
        }
    },

    selectGrain(grain) {
        this.grain = grain;

        if (!this.requested[this.keyOf(grain)]) {
            this.requestGrain(grain);
        }

        this.nameOnPage(grain);
    },

    // The page's ?grain= and ?order= are how a reader shares the table on screen. loadGrain writes
    // both on the server, but a switch served from cache sends no request, so the URL kept naming
    // the last table fetched: back on a cached default order, a shared link still opened on the rise
    // order. A local $wire write moves the URL at once and reaches the server with the next request.
    // The order is written only once an answer settled it: before the first one the page is running
    // the ?order= it was opened with, and writing the provisional default would throw that away.
    nameOnPage(grain) {
        if (grain !== this.grain) {
            return;
        }

        this.$wire.grain = grain;

        const answer = this.grainPayloads[this.keyOf(grain)];

        if (grain in this.orders || (answer && !answer.error)) {
            this.$wire.grainOrder = this.orderOf(grain);
        }

        // The segment flags follow the same rule. Every flag property is written, not just the
        // chosen segment's: a flag left set from another grain would ride along in a shared link and
        // name a segment the table on screen was never narrowed by. Each property's value comes from
        // the chosen segment, never from the flag being walked, because both AI segments share ?ai=:
        // a walk that wrote each flag's own on-or-off could let the one not chosen null what the
        // chosen one wrote.
        const settled = grain in this.segments;
        const offered = this.segmentsOf(grain);
        const flags = Object.entries(this.segmentFlags);
        const chosen = this.segmentFlags[this.segments[grain]];

        flags.forEach(([, { property }]) => {
            if (settled) {
                this.$wire[property] = chosen && chosen.property === property ? chosen.value : null;

                return;
            }

            // Not settled: the page is still running the flag it was opened with, so it is left as
            // it is wherever this grain offers a segment written to it, and cleared everywhere else.
            if (!flags.some(([segment, flag]) => flag.property === property && offered.includes(segment))) {
                this.$wire[property] = null;
            }
        });

        // ?columns= follows the same rule, so a reader can share the table they built. Written only
        // once a set is settled: before that the page is running the ?columns= it was opened with,
        // or the set this reader saved, and naming the provisional defaults would throw that away.
        if (grain in this.selected) {
            const token = this.columnsTokenOf(grain);
            this.$wire.grainColumns = token === COLUMNS_DEFAULT ? null : token;
        }

        // ?rows= the same way, and only where the reader left the grain's own default: the page
        // normalises the default to no parameter, so writing it would only move the URL back and forth.
        if (grain in this.rowSets) {
            this.$wire.grainRowSet = this.rowSets[grain] === this.defaultRowSetOf(grain) ? null : this.rowSets[grain];
        } else if (!(grain in this.rowSetDefaults)) {
            this.$wire.grainRowSet = null;
        }
    },

    // The Retry button lives INSIDE the box statusOf(g) stops returning 'failed' for on the very
    // next line, so the press destroys the element holding focus. Park it on the grain's own tab
    // first: the tab is rendered in every state, is a real tab stop already, sits immediately
    // before the panel being reloaded, and is where focus lands after an ordinary grain switch, so
    // Retry and a switch leave the reader in the same place. What happened is carried by the card's
    // role="status" region, not by the node focus moves to.
    retry(grain) {
        this.parkFocus(`${this.id}-tab-${grain}`);
        const key = this.keyOf(grain);
        delete this.grainPayloads[key];
        delete this.morePending[key];
        delete this.moreFailed[key];
        this.requestGrain(grain);
    },

    orderOf(grain) {
        return this.orders[grain] || ORDER_DEFAULT;
    },

    // ---------------------------------------------------------------- the columns the table shows

    // Every column the grain offers, from the answer on screen or, while one is loading, from any
    // answer already held for this grain — the chooser must stay on screen (and keep focus) while
    // the table it changed is fetched, exactly as the order control does.
    columnOptionsOf(grain) {
        const current = this.payloadFor(grain);

        if (current && Array.isArray(current.columnOptions) && current.columnOptions.length > 0) {
            return current.columnOptions;
        }

        const other = Object.entries(this.grainPayloads).find(
            ([key, payload]) => key.startsWith(`${grain}|`) && Array.isArray(payload.columnOptions) && payload.columnOptions.length > 0,
        );

        return other ? other[1].columnOptions : [];
    },

    isColumnsOffered(grain) {
        return this.columnOptionsOf(grain).length > 1;
    },

    // The measures on, in the grain's declared order. Before the reader has chosen and before an
    // answer has named a set, that is the options' own defaults — so the chooser draws ticked
    // boxes over a loading card rather than an empty list.
    chosenColumnsOf(grain) {
        if (grain in this.selected) {
            return this.selected[grain];
        }

        return this.columnOptionsOf(grain)
            .filter((option) => option.isDefault)
            .map((option) => option.key);
    },

    // What the answer is keyed on. A set that IS the grain's defaults hashes to COLUMNS_DEFAULT —
    // the key the page's own first request asked under — so a reader who unticks a column and ticks
    // it back lands on the table the browser already holds instead of paying for it twice.
    columnsTokenOf(grain) {
        if (!(grain in this.selected)) {
            return COLUMNS_DEFAULT;
        }

        const token = this.selected[grain].join(",");

        return token === this.defaultColumnsTokenOf(grain) ? COLUMNS_DEFAULT : token;
    },

    defaultColumnsTokenOf(grain) {
        if (grain in this.defaults) {
            return this.defaults[grain];
        }

        return this.columnOptionsOf(grain)
            .filter((option) => option.isDefault)
            .map((option) => option.key)
            .join(",");
    },

    isColumnChosen(grain, key) {
        return this.chosenColumnsOf(grain).includes(key);
    },

    // A column the site cannot answer, or the last one still ticked: a grain with no columns is not
    // a table, and the page refuses an empty set, so the control says so rather than offering a
    // press that would be thrown away. The row's own hint line carries the reason in words.
    isColumnLocked(grain, key) {
        const option = this.columnOptionsOf(grain).find((item) => item.key === key);

        if (option && option.isAvailable === false) {
            return true;
        }

        const chosen = this.chosenColumnsOf(grain);

        return chosen.length === 1 && chosen[0] === key;
    },

    columnInputId(grain, key) {
        return `${this.id}-column-${grain}-${key}`;
    },

    // What a column's second line says: which source answers it, or why it cannot be chosen.
    columnHintOf(grain, option) {
        if (option.isAvailable === false) {
            return this.strings.columnUnavailable.replace(":source", option.sourceLabel);
        }

        return this.isColumnLocked(grain, option.key) ? this.strings.columnLast : option.sourceLabel;
    },

    // What a column MEANS, for the header's accessible name. Looked up from columnOptions rather
    // than read off the column itself: a table column is one entry of payload.columns, which every
    // breakdown card shares, and putting a grain-only sentence there would demand the field on all
    // of their payloads. Both blocks are on the same answer, so the join costs nothing.
    columnDescriptionOf(grain, key) {
        const option = this.columnOptionsOf(grain).find((item) => item.key === key);

        return option && option.description ? option.description : "";
    },

    columnsSummaryOf(grain) {
        return this.strings.columnsChosen
            .replace(":n", String(this.chosenColumnsOf(grain).length))
            .replace(":m", String(this.columnOptionsOf(grain).length));
    },

    // A column set is a different table: different cells, and a different sort on a grain ordered by
    // its first column, so a row index points into another answer and the open row closes.
    toggleColumn(grain, key) {
        if (this.isColumnLocked(grain, key)) {
            return;
        }

        const chosen = this.chosenColumnsOf(grain);
        const next = this.columnOptionsOf(grain)
            .filter((option) => (option.key === key ? !chosen.includes(key) : chosen.includes(option.key)))
            .map((option) => option.key);

        if (next.length === 0) {
            return;
        }

        if (this.openRow && this.openRow.grain === grain) {
            this.closeRow();
        }

        this.selected[grain] = next;

        if (!this.requested[this.keyOf(grain)]) {
            this.requestGrain(grain);
        }

        this.nameOnPage(grain);
    },

    // ---------------------------------------------------------------- the rows past the first page

    pagingOf(grain) {
        const payload = this.payloadFor(grain);

        return payload && payload.paging ? payload.paging : null;
    },

    // Offered while the answer says more rows exist, and while a press for them is still in flight
    // or came back refused — the button is the only thing that changes, so it has to stay put to
    // report either.
    isMoreOffered(grain) {
        const paging = this.pagingOf(grain);

        return this.statusOf(grain) === STATUS_DATA && paging !== null && paging.hasMore === true;
    },

    isMorePending(grain) {
        return this.morePending[this.keyOf(grain)] === true;
    },

    isMoreFailed(grain) {
        return this.moreFailed[this.keyOf(grain)] === true;
    },

    moreLabelOf(grain) {
        if (this.isMorePending(grain)) {
            return this.strings.moreLoading;
        }

        return this.isMoreFailed(grain) ? this.strings.moreFailed : this.strings.more;
    },

    // The press is answered in the same frame: morePending is set before the request leaves, so the
    // button reads as busy without waiting for the page, and the rows on screen never blink.
    loadMore(grain) {
        const paging = this.pagingOf(grain);

        if (!this.isMoreOffered(grain) || this.isMorePending(grain)) {
            return;
        }

        const key = this.keyOf(grain);
        this.morePending[key] = true;
        delete this.moreFailed[key];
        this.requestGrain(grain, paging.page + 1);
    },

    segmentOf(grain) {
        return this.segments[grain] || SEGMENT_ALL;
    },

    // The segments this grain can be narrowed to for this site; a grain that offers none has no
    // control at all, and one whose segment this site cannot use says why instead (the card renders
    // that sentence from the same map).
    segmentsOf(grain) {
        return this.grainSegments[grain] || [];
    },

    isSegmentOffered(grain, segment) {
        return this.segmentsOf(grain).includes(segment);
    },

    // Works with the order: the (order, segment) pair names the answer, so switching either keeps the
    // other, and each pair is cached on its own. A row index points into one answer's rows, so an
    // open row closes.
    toggleSegment(grain, segment) {
        if (!this.isSegmentOffered(grain, segment)) {
            return;
        }

        const next = this.segmentOf(grain) === segment ? SEGMENT_ALL : segment;

        if (this.openRow && this.openRow.grain === grain) {
            this.closeRow();
        }

        this.segments[grain] = next;

        if (!this.requested[this.keyOf(grain)]) {
            this.requestGrain(grain);
        }

        this.nameOnPage(grain);
    },

    // The orders the grain's answers offer in this context, from whichever answer is cached, so the
    // control stays on screen (and keeps focus) while the other order loads.
    //
    // `payload.orders` is grain identity, stamped by PayloadEnvelope::forGrain() on every answer
    // including an unbound, empty or failed one. It used to be read off `payload.comparison`, which
    // is written from the rows and only when a compared column survived, so the control vanished on
    // the two answers a reader most needs it on: a table an order's thresholds narrowed to nothing,
    // and any table with the compared column hidden. The cross-answer scan below is what hid that:
    // it found a comparison on some OTHER cached answer for the same grain, so the control stayed
    // put mid-session and disappeared on a reload.
    orderOptionsOf(grain) {
        const current = this.payloadFor(grain);

        if (current && Array.isArray(current.orders) && current.orders.length > 0) {
            return current.orders;
        }

        const other = Object.entries(this.grainPayloads).find(
            ([key, payload]) => key.startsWith(`${grain}|`) && Array.isArray(payload.orders) && payload.orders.length > 0,
        );

        return other ? other[1].orders : [];
    },

    orderLabelOf(grain) {
        const option = this.orderOptionsOf(grain).find((item) => item.key === this.orderOf(grain));

        return option ? option.label : "";
    },

    // A row index points into one order's rows, so an open row closes when the order changes.
    selectOrder(grain, order) {
        if (order === this.orderOf(grain) || !this.orderOptionsOf(grain).some((item) => item.key === order)) {
            return;
        }

        if (this.openRow && this.openRow.grain === grain) {
            this.closeRow();
        }

        this.orders[grain] = order;

        if (!this.requested[this.keyOf(grain)]) {
            this.requestGrain(grain);
        }

        this.nameOnPage(grain);
    },

    // The toggles the "Order rows by" control draws: the answer's orders less the header sorts, which
    // the headers draw, and less Biggest opportunity while the table is narrowed to branded queries,
    // where that order still runs and lists nothing, because every branded query is left out of it
    // (D11). Hidden rather than refused, so no order changes what it means.
    toggleOrdersOf(grain) {
        return this.orderOptionsOf(grain).filter(
            (option) => columnOrderParts(option.key) === null && !(option.key === ORDER_OPPORTUNITY && this.isBrandedOnly(grain)),
        );
    },

    // Whether the table reads branded queries alone: the Query filter on Branded queries, as the
    // answer says it was applied, or the Branded queries segment, should it be offered again.
    isBrandedOnly(grain) {
        if (this.segmentOf(grain) === "branded") {
            return true;
        }

        const payload = this.payloadFor(grain) || this.heldPayloadOf(grain);
        const filters = (payload && payload.query && payload.query.filters) || [];

        return filters.some((filter) => filter.dimension === this.textFilterDimension && filter.operator === "branded");
    },

    // Any answer held for the grain, for what does not change between its orders, sets and columns.
    heldPayloadOf(grain) {
        const held = Object.entries(this.grainPayloads).find(([key]) => key.startsWith(`${grain}|`));

        return held ? held[1] : null;
    },

    // ---------------------------------------------------------------- sorting by a column

    // The column the default order ranks by: the table's first, as the statement orders by it.
    firstColumnOf(grain) {
        const column = this.tableColumnsOf(grain).find((item) => !item.isChange);

        return column ? column.key : null;
    },

    sortOf(grain) {
        return sortOf(this.orderOf(grain), this.firstColumnOf(grain));
    },

    ariaSortOf(grain, key) {
        const sort = this.sortOf(grain);

        if (!sort || sort.measure !== key) {
            return "none";
        }

        return sort.direction === SORT_ASCENDING ? "ascending" : "descending";
    },

    // The arrow beside a sorted header, decoration only: the header's aria-sort says the same.
    sortArrowOf(grain, key) {
        const sort = this.sortOf(grain);

        if (!sort || sort.measure !== key) {
            return "";
        }

        return sort.direction === SORT_ASCENDING ? "↑" : "↓";
    },

    // A header press: the first sorts its column best first (largest first, lowest first for
    // Position), the next reverses it. Every row is sorted, by the page, never the 25 on screen.
    sortColumn(grain, key) {
        const sort = this.sortOf(grain);
        const direction = sort && sort.measure === key
            ? (sort.direction === SORT_DESCENDING ? SORT_ASCENDING : SORT_DESCENDING)
            : ((this.sortFirst[grain] || {})[key] || SORT_DESCENDING);

        this.selectOrder(grain, columnOrder(key, direction, this.firstColumnOf(grain)));
    },

    // ---------------------------------------------------------------- the row set

    defaultRowSetOf(grain) {
        return this.rowSetDefaults[grain] || ROW_SET_ALL;
    },

    // The reader's row set for the grain: their choice, or the grain's own default.
    rowSetOf(grain) {
        return this.rowSets[grain] || this.defaultRowSetOf(grain);
    },

    // The row set the grain's answer is keyed by: the reader's, and every row under Biggest
    // opportunity, which reads every row (D10). Only the reader's own state, never an answer held:
    // a key read off the answers would move the moment the first one landed, and the card would
    // look that answer up under a key it was not filed under. Where the page cannot filter by clicks
    // at all (Discover, or Search Console not connected), receiveGrain() files its every-row answer
    // under this key, since it is the answer to the question asked.
    rowSetKeyOf(grain) {
        if (!(grain in this.rowSetDefaults) || this.orderOf(grain) === ORDER_OPPORTUNITY) {
            return ROW_SET_ALL;
        }

        return this.rowSetOf(grain);
    },

    // The row control is drawn exactly where pressing it can change the table: the answer on screen
    // offers the clicked set (its `rowSets`). While the other set loads there is no answer on screen,
    // and the control stays where it was, with the reader's focus, as the order control does.
    isRowSetOffered(grain) {
        if (!(grain in this.rowSetDefaults) || this.orderOf(grain) === ORDER_OPPORTUNITY) {
            return false;
        }

        const payload = this.payloadFor(grain);

        return !payload || !Array.isArray(payload.rowSets) || payload.rowSets.includes(ROW_SET_CLICKED);
    },

    // Pressed means every row: "Include rows without clicks".
    isEveryRowIncluded(grain) {
        return this.rowSetKeyOf(grain) === ROW_SET_ALL;
    },

    // Works with the order, segment and columns: the set names the answer, so switching it keeps them,
    // and each set is cached on its own. A row index points into one answer's rows, so an open row
    // closes.
    toggleRowSet(grain) {
        if (!this.isRowSetOffered(grain)) {
            return;
        }

        if (this.openRow && this.openRow.grain === grain) {
            this.closeRow();
        }

        this.rowSets[grain] = this.isEveryRowIncluded(grain) ? ROW_SET_CLICKED : ROW_SET_ALL;

        if (!this.requested[this.keyOf(grain)]) {
            this.requestGrain(grain);
        }

        this.nameOnPage(grain);
    },

    tabIndex(grain) {
        return this.grains.findIndex((item) => item.key === grain);
    },

    focusTab(event, offset, absolute = null) {
        const tabs = [...this.$root.querySelectorAll('[role="tab"]')];
        const current = tabs.indexOf(event.currentTarget);
        const next = absolute !== null ? absolute : (current + offset + tabs.length) % tabs.length;
        const target = tabs[next < 0 ? tabs.length - 1 : next];

        if (target) {
            target.focus();
        }
    },

    // Move focus onto a node that outlives a state change, BEFORE the change hides the element
    // currently holding it. It has to be before: once x-show has written display:none the browser
    // has already run its unfocusing steps and document.activeElement is <body>, x-show offers no
    // "was hidden" hook to restore from, and reading activeElement afterwards only ever finds
    // <body>. The one instant at which both the focus and a surviving target are knowable is the
    // first statement of the handler.
    //
    // document.getElementById, NOT this.$root.querySelector: Alpine binds $root to the nearest
    // [x-data] ancestor OF THE ELEMENT the expression runs on, not to this component. Every button
    // that parks (both Retry buttons and a row's Filter) sits inside a tabpanel that declares its
    // own x-data="{ g: … }" in explore.blade.php, while the grain tabs live in the tablist, a
    // SIBLING of every tabpanel. A $root lookup would return null for a tab, and the whole fix would be a
    // silent no-op at the site it matters most. focusTab() above gets away with $root only because
    // it is bound on the tabs themselves, where the nearest root IS the <section>. This card's ids
    // are already page-unique (every one is built from this.id), and a plain document lookup is the
    // idiom shipped for exactly this situation in
    // livewire/sites/settings/analytics-sources.blade.php:221.
    //
    // Parked unconditionally, as that idiom does. Clicking a <button> does not focus it on Safari,
    // so a mouse user's focus is elsewhere and this moves it — accepted deliberately: nothing
    // readable at click time tells a keyboard press from a mouse press (:focus-visible is a CSS
    // match, not a queryable state), and landing a mouse user on the tab of the card they just
    // pressed costs far less than dropping a keyboard user onto <body>.
    parkFocus(id) {
        document.getElementById(id)?.focus();
    },

    // A report reload with a new period, filter or search type asks a new question, and one after a
    // source was connected or disconnected (the picker's save) answers the same question from other
    // data. Either way every cached grain is stale in every order: drop them all and ask again for
    // the grain on screen, in the order it is on.
    // The context is compared from the report's payloads, the sources from the availability the
    // page sends beside them; the first report of each only records it.
    receiveReportData(detail) {
        const next = contextOf(Object.values((detail && detail.payloads) || {}));
        const availability = availabilityKeyOf(detail);

        const contextChanged = next !== null && !sameContext(this.context, next);
        const sourcesChanged = availability !== null && this.availability !== null && availability !== this.availability;
        const hadContext = this.context !== null;

        if (availability !== null) {
            this.availability = availability;
        }

        if (contextChanged) {
            this.context = next;
        }

        if (!(contextChanged && hadContext) && !sourcesChanged) {
            return;
        }

        // orders, selected and rowSets survive: the reader's chosen order, columns and rows are part
        // of their question, not of the data. morePending and moreFailed do not — they describe a
        // fetch of rows that no longer exist under this question — and neither do the topics and the
        // Hidden by Google lines, which were read with them.
        this.closeRow();
        this.forgetAnswers();
        this.requestGrain(this.grain);
    },

    // Every answer held for the old question, and what was read beside each.
    forgetAnswers() {
        this.grainPayloads = {};
        this.requested = {};
        this.requestFailed = {};
        this.morePending = {};
        this.moreFailed = {};
        this.topics = {};
        this.hiddenLines = {};
    },

    // The report has claimed its slots and is about to fetch them: every grain on screen answers
    // the OLD question, so the table drops them now rather than sitting on last month's rows
    // until the report's answer arrives. Asking again from here — instead of waiting for
    // receiveReportData — is what lets the tab fill in parallel with the cards above it.
    //
    // Clearing `context` is deliberate and load-bearing: receiveReportData only re-requests when
    // the context CHANGED from one it had, so a null context makes it a no-op and the grain is
    // not fetched twice for one filter change.
    //
    // A load that names a `card` is one chart card's own Interval switch re-running that card
    // alone: the question this table answers has not moved, and closing the reader's open row for
    // it would take away what they were reading. The page's first load (`first`, from mount()) is
    // the one init() already asked this grain for: asking again put a second loadGrain in the
    // same request, as costly as the first.
    receiveReportLoading(detail) {
        if (detail && (detail.card || detail.first)) {
            return;
        }

        this.closeRow();
        this.forgetAnswers();
        this.context = null;
        this.requestGrain(this.grain);
    },

    receiveGrain(detail) {
        if (!detail || !detail.payload || this.tabIndex(detail.grain) === -1) {
            return;
        }

        this.rememberDraftable(detail.grain, detail.draftable);

        const next = contextOf([detail.payload]);

        if (this.context !== null && !sameContext(this.context, next)) {
            this.closeRow();
            this.forgetAnswers();
            this.context = next;
        } else if (this.context !== null) {
            // A GA-only grain binds no search type. Replacing the context with its narrower one
            // would drop the search type the report established, and a later search-type change
            // would then compare equal and leave the Search Console grains stale.
            this.context = { ...next, scopes: { ...this.context.scopes, ...next.scopes } };
        } else {
            this.context = next;
        }

        const payload = detail.payload;
        const grain = detail.grain;
        const asked = this.orderOf(grain);
        const askedSegment = this.segmentOf(grain);
        const askedColumns = this.columnsTokenOf(grain);
        const askedRowSet = this.rowSetKeyOf(grain);
        const askedKey = answerKey(grain, asked, askedSegment, askedColumns, askedRowSet);
        // An answer names the order it ran in query.order. The one exception is a failed answer to
        // a grain's first request: that request named no order and is waiting under the default,
        // so filing the failure under the URL's order would leave the skeleton spinning. Once the
        // grain has an order, a failure is filed under the order it names, or a late failure for
        // the order the reader just left would replace the answer now on screen with a Retry.
        const named = payload.query && payload.query.order ? payload.query.order : null;
        const order = named === null || (payload.error && !(grain in this.orders)) ? asked : named;
        // The segment follows the order's filing rule, but is adopted only from a grain's first
        // answer: the page never swaps a segment it was asked for, so a later answer naming another
        // one is a late answer for the segment the reader left, and must not switch them back.
        const namedSegment = this.segmentsOf(grain).length > 0 ? segmentOfPayload(payload) : null;
        const segment = namedSegment === null || (payload.error && !(grain in this.segments)) ? askedSegment : namedSegment;
        // And so do the columns: an answer names the set it ran in query.columns, which is how the
        // set a reader SAVED (or opened the page with) reaches a card that asked for none. An answer
        // that ran the grain's own defaults is filed under COLUMNS_DEFAULT, the key a reader who has
        // chosen nothing asks under, or every first answer would land under a key no press looks up
        // and the card would ask again for the table it had just been given. A failed first answer
        // stays filed under what was asked, for the order's reason.
        const namedColumns = columnsOfPayload(payload);
        const defaultColumns = (payload.columnOptions || []).filter((option) => option.isDefault).map((option) => option.key).join(",");

        if (defaultColumns !== "") {
            this.defaults[grain] = defaultColumns;
        }

        const columns = namedColumns === null || namedColumns === defaultColumns || (payload.error && !(grain in this.selected))
            ? askedColumns
            : namedColumns;
        // And the row set. An answer that could not offer the set asked for (the page read every row,
        // since it cannot read clicks here) answers the question asked, so it is filed under it; so
        // is a failure, for the order's reason.
        const namedRowSet = rowSetOfPayload(payload);
        const rowSet = namedRowSet === null || payload.error || !offers(payload.rowSets, askedRowSet) ? askedRowSet : namedRowSet;

        if (!payload.error) {
            const isFirstAnswer = !(grain in this.orders);

            if (isFirstAnswer && (payload.comparison || order !== ORDER_DEFAULT)) {
                // The page ran the ?order= it was opened with; adopt it, and forget the default
                // this request was provisionally filed under.
                delete this.requested[askedKey];
                this.orders[grain] = order;
            } else if (!isFirstAnswer && order !== asked && !offers(payload.orders, asked)
                && segment === askedSegment && columns === askedColumns && rowSet === askedRowSet) {
                // The order asked for is not on offer in this answer, and the page ran its default
                // instead: a rise order with the compared column hidden or no query rows for this
                // search type, or a header sort on a column the reader has since hidden.
                //
                // Judged by what the answer OFFERS, never by `order !== asked` alone: a late answer
                // for the order the reader just left still offers the one they are on now, so it
                // cannot move the card off it. And only for an answer to the question on screen in
                // every other part, so a late answer for columns the reader has changed since, which
                // may not offer a sort on a column they just turned on, cannot either.
                delete this.requested[askedKey];
                this.orders[grain] = order;
            }

            if (namedRowSet !== null && !(grain in this.rowSets) && offers(payload.rowSets, ROW_SET_CLICKED) && rowSet === namedRowSet
                && rowSet !== this.defaultRowSetOf(grain)) {
                // The page ran the ?rows= it was opened with. Adopted only from an answer that
                // offered a choice (under Biggest opportunity every row is read whatever was chosen),
                // and only when it is not the grain's own default, as an order is: adopting the
                // default would name it in every later request, and loadGrain('pages') would stop
                // being the call it always was.
                if (rowSet !== askedRowSet) {
                    delete this.requested[askedKey];
                }

                this.rowSets[grain] = rowSet;
            }

            if (namedSegment !== null && !(grain in this.segments)) {
                // The page ran the segment flag it was opened with.
                if (segment !== askedSegment) {
                    delete this.requested[askedKey];
                }

                this.segments[grain] = segment;
            }

            if (namedColumns !== null && namedColumns !== defaultColumns && !(grain in this.selected)) {
                // The page ran the ?columns= it was opened with, or this reader's saved set.
                if (columns !== askedColumns) {
                    delete this.requested[askedKey];
                }

                this.selected[grain] = [...payload.query.columns];
            }
        }

        const key = answerKey(grain, order, segment, columns, rowSet);
        const held = this.grainPayloads[key];
        const page = payload.paging ? payload.paging.page : 1;
        // A later page is an ADDITION to the table already on screen, not a replacement, so the
        // reader keeps the rows they were reading and never pays to fetch them again. Anything
        // that failed on the way to page 2 leaves those rows exactly where they are and says so
        // on the Show-more button alone.
        const isMore = page > 1 && held && held.hasData;

        delete this.morePending[key];

        if (isMore && payload.error) {
            this.moreFailed[key] = true;
            this.nameOnPage(grain);

            return;
        }

        delete this.moreFailed[key];

        if (this.openRow && this.openRow.grain === grain && order === this.orderOf(grain) && segment === this.segmentOf(grain) && columns === this.columnsTokenOf(grain) && rowSet === this.rowSetKeyOf(grain) && !isMore) {
            this.closeRow();
        }

        this.requested[key] = true;
        delete this.requestFailed[key];
        this.grainPayloads[key] = isMore ? withMoreRows(held, payload) : payload;
        this.rememberTopics(grain, detail);

        // The Hidden by Google line travels with every answer of the Queries grain, the same for
        // every order, row set and page of one question; a failed answer carries none and leaves the
        // line the question already had.
        if (Object.prototype.hasOwnProperty.call(detail, "hidden")) {
            this.hiddenLines = { ...this.hiddenLines, [grain]: detail.hidden || null };
        }

        // The response that carried this answer also wrote the order it ran in onto the page, which
        // is wrong when the reader has since gone back to a cached order.
        this.nameOnPage(grain);
    },

    // Each row's topic, merged into the grain's map as pages of rows arrive; null, when the answer
    // says the site has no topic to show, turns the Topic column off. A failed answer carries nothing.
    rememberTopics(grain, detail) {
        if (!Object.prototype.hasOwnProperty.call(detail, "topics")) {
            return;
        }

        if (!Array.isArray(detail.topics)) {
            this.topics = { ...this.topics, [grain]: null };

            return;
        }

        const map = new Map(this.topics[grain] instanceof Map ? this.topics[grain] : []);

        detail.topics.forEach((entry) => map.set(entry.key, entry.name));
        this.topics = { ...this.topics, [grain]: map };
    },

    // Whether the grain's table shows a Topic column, and one row's topic in it.
    hasTopicColumn(grain) {
        return this.topics[grain] instanceof Map;
    },

    topicOf(grain, key) {
        return this.hasTopicColumn(grain) ? this.topics[grain].get(key) || "" : "";
    },

    // A21: this site's topics changed (an edit, or a grouping with AI that ended), so the Topic column
    // is read again for the rows already here (Analytics::exploreTopics()) and swapped in place. The
    // rows, their extra pages and an open drill stay as they are, where asking each grain again would
    // have closed them. A column can come on (the site's first topics) or go off, as a fresh answer's
    // would.
    async refreshTopics() {
        const sets = {};

        Object.keys(this.topics).forEach((grain) => {
            const payload = this.payloadFor(grain);

            if (payload && payload.hasData && payload.dimension) {
                sets[`grain:${grain}`] = { dimension: payload.dimension.key, keys: payload.rows.map((row) => String(row.key)) };
            }
        });

        const drill = this.drillAnswer && this.drillAnswer.payload;
        const drillRow = this.openRow;

        if (drill && drill.hasData && drill.dimension) {
            sets.drill = { dimension: drill.dimension.key, keys: drill.rows.map((row) => String(row.key)) };
        }

        if (Object.keys(sets).length === 0) {
            return;
        }

        let answer;

        try {
            answer = await this.$wire.exploreTopics(sets);
        } catch (error) {
            // Only the column's names are behind: the rows are right, and each grain's next answer
            // carries its topics again.
            return;
        }

        const asMap = (list) => (Array.isArray(list) ? new Map(list.map((entry) => [entry.key, entry.name])) : null);
        const topics = { ...this.topics };

        Object.entries(answer || {}).forEach(([id, list]) => {
            if (id.startsWith("grain:")) {
                topics[id.slice("grain:".length)] = asMap(list);
            }
        });
        this.topics = topics;

        // The drill's names only while the row they were read for is still the open one.
        if (answer && Object.prototype.hasOwnProperty.call(answer, "drill") && this.drillAnswer && this.openRow === drillRow) {
            this.drillAnswer = { ...this.drillAnswer, topics: asMap(answer.drill) };
        }
    },

    // The line under the Queries table (Analytics::hiddenByGoogleLine()), or null for none. Under
    // Discover and Google News it is the note the table's own empty state already prints, and it is
    // not printed twice.
    hiddenLineOf(grain) {
        const line = this.hiddenLines[grain] || null;

        if (!line || (line.state === "note" && this.notesOf(grain).includes(line.text))) {
            return null;
        }

        return line;
    },

    payloadFor(grain) {
        return this.grainPayloads[this.keyOf(grain)] || null;
    },

    statusOf(grain) {
        const payload = this.payloadFor(grain);

        if (payload) {
            return statusOfPayload(payload);
        }

        const key = this.keyOf(grain);

        if (this.requestFailed[key]) {
            return STATUS_FAILED;
        }

        return this.requested[key] ? STATUS_LOADING : STATUS_IDLE;
    },

    columnsOf(grain) {
        const payload = this.payloadFor(grain);

        return payload && payload.hasData ? payload.columns : [];
    },

    tableColumnsOf(grain) {
        return tableColumns(this.payloadFor(grain));
    },

    tableCellsOf(grain, row) {
        return tableCells(this.payloadFor(grain), row);
    },

    // Every column a full-width cell spans: the toggle, the row's name, the Topic column where the
    // site has topics, and the figures.
    colspanOf(grain) {
        return this.tableColumnsOf(grain).length + 2 + (this.hasTopicColumn(grain) ? 1 : 0);
    },

    // Tailwind reads class names from this file, so each is written out whole. The tone already
    // accounts for a measure where lower is better; the arrow and the spoken word carry the
    // direction, so the colour is never the only signal.
    changeToneClass(change) {
        if (!change) {
            return "text-gray-700";
        }

        return change.tone === "good" ? "text-green-800" : change.tone === "bad" ? "text-red-800" : "text-gray-700";
    },

    changeArrow(change) {
        if (!change) {
            return "";
        }

        return change.direction === "up" ? "▲" : change.direction === "down" ? "▼" : "–";
    },

    changeDirectionLabel(change) {
        if (!change) {
            return "";
        }

        return change.direction === "up" ? this.strings.changeUp : change.direction === "down" ? this.strings.changeDown : this.strings.changeFlat;
    },

    rowsOf(grain) {
        const payload = this.payloadFor(grain);

        return payload && payload.hasData ? payload.rows : [];
    },

    otherOf(grain) {
        const payload = this.payloadFor(grain);

        return payload && payload.hasData ? payload.other : null;
    },

    notesOf(grain) {
        const payload = this.payloadFor(grain);

        return payload ? payload.notes : [];
    },

    dimensionLabelOf(grain) {
        const payload = this.payloadFor(grain);

        return payload && payload.dimension ? payload.dimension.label : "";
    },

    // Which chip a row of this grain sets, or null when the report has no filter for the dimension
    // it groups by. Null hides the control rather than showing one that cannot do anything.
    filterDimensionFor(grain) {
        return filterDimensionOf(this.payloadFor(grain), this.filterDimensions);
    },

    // A row is already the filter when the answer on screen was narrowed to its own value. The
    // control then reads as pressed and clears the chip instead of re-applying it, which is the
    // only way back out of a one-row table without reaching for the filter bar.
    isRowFiltered(grain, row) {
        const dimension = this.filterDimensionFor(grain);

        return dimension !== null && isFilteredTo(this.payloadFor(grain), dimension, row.key);
    },

    // One name whatever the state, because this is a toggle button and aria-pressed says the
    // state. A name that changed to "Clear..." when pressed would stop containing the label a
    // voice-control user just read off the screen.
    filterRowLabel(grain) {
        return this.strings.filterRow.replace(":dimension", this.dimensionLabelOf(grain).toLowerCase());
    },

    // The page owns filter state: it re-resolves every dataset, reloads every card and rewrites the
    // URL, then pushes a site-analytics-data event whose changed context makes this card drop its
    // cached answers and ask again. Nothing is updated locally, or the table and the chips above it
    // would disagree until the next reload.
    toggleRowFilter(grain, row) {
        const dimension = this.filterDimensionFor(grain);

        // Only ever null when a race repainted the table between the press and the handler: the
        // control is rendered under the same condition. The (other) row needs no guard here - it
        // is drawn by its own template, outside the loop that carries this control, because a
        // grouped remainder has no single value to filter by.
        if (dimension === null) {
            return;
        }

        // Same trap as retry(): setFilter() reloads the whole report, receiveReportLoading() empties
        // this grain's answer, and the x-show wrapping the table hides this button with it. Parked
        // on the grain's tab, where Retry leaves the reader, because the press re-draws the same
        // panel; and after the guard, because a press that changes nothing destroys nothing.
        const isFiltered = this.isRowFiltered(grain, row);
        this.parkFocus(`${this.id}-tab-${grain}`);

        // The typed dimension has its own two actions, the ones the Query dialog uses: setFilter()
        // takes only the picked dimensions and ignores this one by design.
        if (dimension === this.textFilterDimension) {
            if (isFiltered) {
                this.$wire.clearQueryFilter();
            } else {
                this.$wire.setQueryFilter(this.rowQueryOperator, row.key);
            }

            return;
        }

        this.$wire.setFilter(dimension, isFiltered ? "" : row.key);
    },

    // Replaced rather than added to in place, so Alpine sees the grain's entry change.
    rememberDraftable(grain, keys) {
        if (!Array.isArray(keys) || keys.length === 0) {
            return;
        }

        this.draftable = { ...this.draftable, [grain]: new Set([...(this.draftable[grain] || []), ...keys]) };
    },

    isDraftable(grain, row) {
        const keys = this.draftable[grain];

        return Boolean(keys && row && keys.has(row.key));
    },

    // Opens the dialog the page mounts beside this card (Livewire\Sites\DraftArticle) on this row's
    // search, read over the window and search type the table was answered with, for the reason
    // rowPageReportUrl() reads them off the payload: this card is wire:ignore'd, so the page's own
    // period rendered into it would go stale. Focus stays on the pressed control, which is where the
    // dialog's trap returns it on close.
    openDraftArticle(grain, row) {
        const payload = this.payloadFor(grain);

        if (!payload || !this.isDraftable(grain, row)) {
            return;
        }

        const period = payload.period || {};

        window.dispatchEvent(new CustomEvent("open-draft-article", {
            detail: {
                query: row.key,
                searchType: (payload.scopes || {}).search_type || null,
                period: period.presetKey || null,
                start: period.start || null,
                end: period.end || null,
            },
        }));
    },

    // The grain's WHOLE row count, not the rows on screen: a tab that read "25" after one page and
    // "50" after the reader pressed Show more would be counting the fetch, not the site's pages.
    rowCountOf(grain) {
        if (this.statusOf(grain) !== STATUS_DATA) {
            return "";
        }

        const paging = this.pagingOf(grain);

        return String(paging ? paging.total : this.rowsOf(grain).length);
    },

    rowCountLabelOf(grain) {
        const count = this.rowCountOf(grain);

        if (count === "") {
            return "";
        }

        return ", " + (count === "1" ? this.strings.rowCountOne : this.strings.rowCount).replace(":n", count);
    },

    emptyCopyOf(grain) {
        const notes = this.notesOf(grain);

        return notes.length > 0 ? notes.join(" ") : this.strings.empty;
    },

    // "Showing 25 of 412" once the payload's paging block says there are more rows than the reader
    // has, and plainly "Showing 3 rows." when it does not — a grain holding three rows must never
    // read "Showing 3 of 3", which invites a reader to look for the rest. When the grain hit its
    // ceiling the footer also says more exist beyond it.
    footerOf(grain) {
        const payload = this.payloadFor(grain);

        if (!payload || !payload.hasData) {
            return "";
        }

        const count = payload.rows.length;
        const total = payload.paging ? payload.paging.total : count;
        const shown = total > count
            ? this.strings.showingOf.replace(":n", String(count)).replace(":total", String(total))
            : (count === 1 ? this.strings.showingOne : this.strings.showingMany).replace(":n", String(count));

        return payload.isLimitReached ? `${shown} ${this.strings.limitReached}` : shown;
    },

    // Tailwind reads class names from this file, so both are written out whole.
    alignClass(grain, cellIndex) {
        const column = this.tableColumnsOf(grain)[cellIndex];

        return column && column.align === "left" ? "text-left" : "text-right";
    },

    labelOf(grain) {
        const item = this.grains.find((entry) => entry.key === grain);

        return item ? item.label : grain;
    },

    // The grain's name with its order, where it offers more than one, so choosing an order
    // changes what the status region says and a screen reader hears the table was reordered.
    orderedLabelOf(grain) {
        const name = this.labelOf(grain);
        // The segment is said with the grain's name, so switching it changes what the status region
        // announces, exactly as choosing an order does.
        const segmentLabel = (this.strings.segmentLabels || {})[this.segmentOf(grain)];
        const label = segmentLabel
            ? this.strings.orderedGrain.replace(":grain", name).replace(":order", segmentLabel)
            : name;
        const order = this.orderOptionsOf(grain).length > 1 ? this.orderLabelOf(grain) : "";

        return order === "" ? label : this.strings.orderedGrain.replace(":grain", label).replace(":order", order);
    },

    announcement() {
        const grain = this.grain;
        const label = this.orderedLabelOf(grain);
        const openRow = this.openRow && this.openRow.grain === grain ? this.rowsOf(grain)[this.openRow.index] : null;

        if (openRow) {
            switch (this.rowStatus()) {
                case STATUS_LOADING:
                    return this.strings.rowLoading.replace(":row", openRow.label);
                case STATUS_FAILED:
                    return this.strings.rowFailed.replace(":row", openRow.label);
                case STATUS_EMPTY:
                    return this.rowEmptyCopy();
                default:
                    return this.strings.rowHeading.replace(":row", openRow.label);
            }
        }

        switch (this.statusOf(grain)) {
            case STATUS_LOADING:
                return this.strings.loading.replace(":grain", label);
            case STATUS_EMPTY:
                return `${label}: ${this.emptyCopyOf(grain)}`;
            case STATUS_DATA:
                return `${label}: ${this.footerOf(grain)}`;
            default:
                return "";
        }
    },

    alertText() {
        return this.statusOf(this.grain) === STATUS_FAILED ? this.strings.failed.replace(":grain", this.labelOf(this.grain)) : "";
    },

    // Row ids are index-derived because row keys are raw dimension values ('/pricing',
    // 'google / organic') that are not valid id fragments.
    rowId(grain, index, part) {
        return `${this.id}-${grain}-row-${index}-${part}`;
    },

    isRowOpen(grain, index) {
        return this.openRow !== null && this.openRow.grain === grain && this.openRow.index === index;
    },

    toggleRow(grain, index) {
        if (this.isRowOpen(grain, index)) {
            this.closeRow();

            return;
        }

        const row = this.rowsOf(grain)[index];

        if (!row) {
            return;
        }

        this.closeRow();
        this.openRow = { grain, index, key: row.key };
        // Two calls in one tick, which Livewire sends as one request: the rows the row opens to, on
        // the Explore limiter, and its over-time chart, on the page limiter, as it always was.
        this.requestDrill(grain, row);
        this.requestRow(grain, row.key);
    },

    // Clearing openRow is the whole teardown: the open row's panel is x-if'd on isRowOpen() and
    // its chart on isRowChartShown(), so Alpine removes the chart's SiteAnalyticsReport scope and
    // its chart host, whose destroy() unmounts the chart and parks its id on 'loading'. Its drill
    // rows are drawn from drillAnswer, so they go with it.
    closeRow() {
        this.openRow = null;
        this.rowAnswer = null;
        this.drillAnswer = null;
        this.drillRequestFailed = false;
        this.rowRequestFailed = false;
        this.rowInterval = null;
        this.rowIntervalSwitch = null;
        this.isRowReasking = false;
    },

    // The open row's drill, accepted only for the row that is open: a late answer for a row the
    // reader has since closed or left must not draw its rows under another.
    receiveDrill(detail) {
        if (!detail || !this.openRow || detail.grain !== this.openRow.grain || detail.value !== this.openRow.key) {
            return;
        }

        this.drillAnswer = {
            payload: detail.payload || null,
            topPage: detail.topPage || null,
            topics: Array.isArray(detail.topics) ? new Map(detail.topics.map((entry) => [entry.key, entry.name])) : null,
        };
        this.drillRequestFailed = false;
    },

    retryDrill() {
        if (!this.openRow) {
            return;
        }

        // retryRow()'s trap: this button is in the line drillStatus() replaces, so focus is parked
        // on the row's own toggle before it goes.
        const row = this.rowsOf(this.openRow.grain)[this.openRow.index];

        this.parkFocus(this.rowId(this.openRow.grain, this.openRow.index, "toggle"));
        this.drillAnswer = null;
        this.requestDrill(this.openRow.grain, row);
    },

    drillStatus() {
        if (!this.openRow || !(this.openRow.grain in this.drillGrains)) {
            return STATUS_IDLE;
        }

        if (this.drillAnswer === null) {
            return this.drillRequestFailed ? STATUS_FAILED : STATUS_LOADING;
        }

        return this.drillAnswer.payload ? statusOfPayload(this.drillAnswer.payload) : STATUS_FAILED;
    },

    // Whether the row at this index opens to rows of its own, and they are the ones on screen.
    isDrillShown(grain, index) {
        return this.isRowOpen(grain, index) && grain in this.drillGrains;
    },

    // The rows under the open row, when they have landed.
    drillRowsOf(grain, index) {
        if (!this.isDrillShown(grain, index) || this.drillStatus() !== STATUS_DATA) {
            return [];
        }

        return this.drillAnswer.payload.rows || [];
    },

    drillCellsOf(grain, child) {
        return drillCells(this.tableColumnsOf(grain), child);
    },

    isTopPage(child) {
        const topPage = this.drillAnswer && this.drillAnswer.topPage;

        return Boolean(topPage && topPage.path && child && child.key === topPage.path);
    },

    drillTopicOf(child) {
        const topics = this.drillAnswer && this.drillAnswer.topics;

        return topics ? topics.get(child.key) || "" : "";
    },

    // What the drill's line under its rows says: the advice for a query's top page, why no page is
    // marked, and how many rows a long list holds past the ones shown.
    drillLinesOf(grain, index) {
        if (!this.isDrillShown(grain, index) || this.drillAnswer === null) {
            return [];
        }

        const lines = [];
        const topPage = this.drillAnswer.topPage;
        const payload = this.drillAnswer.payload;

        if (topPage && topPage.advice) {
            lines.push(topPage.advice);
        }

        if (topPage && topPage.note) {
            lines.push(topPage.note);
        }

        if (payload && payload.hasData && payload.paging && payload.paging.total > payload.rows.length) {
            lines.push(this.strings.drillMore.replace(":shown", String(payload.rows.length)).replace(":total", String(payload.paging.total)));
        }

        return lines;
    },

    // The words beside the open row's rows: which rows they are while they load or when they fail,
    // and what the drill found when it found nothing. A query that Google showed no page for says so
    // in A2's words; a page no search showed says so in this card's.
    drillCaptionOf(grain, index) {
        const row = this.rowsOf(grain)[index];
        const label = row ? row.label : "";

        switch (this.drillStatus()) {
            case STATUS_LOADING:
                return this.strings.drillLoading.replace(":row", label);
            case STATUS_FAILED:
                return this.strings.drillFailed.replace(":row", label);
            case STATUS_EMPTY:
                return this.drillGrains[grain] === "pages" ? this.strings.drillNoPages : this.strings.drillNoQueries;
            default:
                return (this.drillGrains[grain] === "pages" ? this.strings.drillPagesOf : this.strings.drillQueriesOf).replace(":row", label);
        }
    },

    // Every element the row's toggle opens, for its aria-controls: the drill's caption, its rows, and
    // the panel with the chart.
    rowControlsOf(grain, index) {
        if (!this.isRowOpen(grain, index)) {
            return null;
        }

        const ids = [];

        if (this.isDrillShown(grain, index)) {
            ids.push(this.rowId(grain, index, "drill"));
            this.drillRowsOf(grain, index).forEach((child, childIndex) => ids.push(this.rowId(grain, index, `drill-${childIndex}`)));
        }

        ids.push(this.rowId(grain, index, "panel"));

        return ids.join(" ");
    },

    receiveRow(detail) {
        if (!detail || !this.openRow || detail.grain !== this.openRow.grain || detail.value !== this.openRow.key) {
            return;
        }

        // A repeated answer for the open row leaves isRowChartShown() true, so no second chart
        // mounts over the first: the mounted chart hears the same event and redraws its own
        // instance, dropping an errored payload beside a drawable one as rowChartPayloads() does on
        // mount (sar_ingestRow()). The one thing that re-requests an open row whose chart is
        // showing is the chart's own Interval switch (pickRowInterval()), which asks the same row
        // again; retryRow() runs only after a failure, when the chart is not mounted. Livewire
        // answers one component's calls in order, so the last pick's answer is the last to land,
        // and another row's late answer cannot reach a mounted chart.
        this.rowAnswer = detail.payloads || {};
        this.rowIntervalSwitch = detail.interval || null;
        this.isRowReasking = false;
        this.rowRequestFailed = false;
    },

    // The open row's chart picked an interval. Its answer is kept rather than cleared, so the chart
    // and the switch the reader just pressed stay mounted, and keep focus, while the row is asked
    // again; the chart shows loading until the new answer lands.
    pickRowInterval(interval) {
        if (!this.openRow || typeof interval !== "string") {
            return;
        }

        this.rowInterval = interval;
        this.isRowReasking = true;
        this.requestRow(this.openRow.grain, this.openRow.key);
    },

    retryRow() {
        if (!this.openRow) {
            return;
        }

        // Same trap as retry(): this button is inside the box rowStatus() hides two lines down.
        // Parked AFTER the early return, never before it — with no open row there is no row to
        // return to, and nothing was destroyed to need returning from. The row's own toggle is the
        // target: it is the control that opened this panel, it sits outside the box, and being a
        // <button> it is focusable without any attribute of its own.
        this.parkFocus(this.rowId(this.openRow.grain, this.openRow.index, "toggle"));
        this.rowAnswer = null;
        this.requestRow(this.openRow.grain, this.openRow.key);
    },

    rowStatus() {
        if (!this.openRow) {
            return STATUS_IDLE;
        }

        if (this.rowAnswer === null) {
            return this.rowRequestFailed ? STATUS_FAILED : STATUS_LOADING;
        }

        const payloads = Object.values(this.rowAnswer);

        if (payloads.length > 0 && payloads.every((payload) => payload.error)) {
            return STATUS_FAILED;
        }

        return payloads.some((payload) => !payload.error && payload.hasData) ? STATUS_DATA : STATUS_EMPTY;
    },

    // The chart exists only while this row is open AND its answer has data. Gating the mount on
    // the accepted answer, rather than on the row opening, keeps the chart out of the DOM while
    // the request is in flight, which is when an answer for a row the reader has since left
    // arrives: the chart's own site-analytics-row listener cannot tell one row's answer from
    // another's, and receiveRow() above is what can. The template hands the chart this answer
    // on mount for the same reason.
    isRowChartShown(grain, index) {
        return this.isRowOpen(grain, index) && this.rowStatus() === STATUS_DATA;
    },

    // What the chart draws: the accepted answer less any payload that failed. The chart treats a
    // single errored payload as a failed figure, so handing it Search Console's error beside
    // Google Analytics' data would replace a drawable series with a Retry that re-runs the page's
    // slots rather than this row.
    rowChartPayloads() {
        return Object.fromEntries(Object.entries(this.rowAnswer || {}).filter(([, payload]) => !payload.error));
    },

    /**
     * Where this row's own page report lives, or '' for a row that is not a page.
     *
     * Built from the payload the table was read with rather than from the page's Livewire state:
     * the card is wire:ignore'd, so a period rendered into it at boot would still say "last 30
     * days" after the reader moved to last 90. The period is named by its preset where the reader
     * chose one, and by its exact dates where they did not, so the report opens on the window the
     * row is a row of.
     */
    rowPageReportUrl(grain, row) {
        const payload = this.payloadFor(grain);

        if (this.pageReportUrl === '' || !row || !payload || !payload.dimension || payload.dimension.key !== 'page') {
            return '';
        }

        const period = payload.period || {};
        const params = new URLSearchParams({ path: row.key });

        if (period.presetKey && period.presetKey !== 'custom') {
            params.set('period', period.presetKey);
        } else {
            params.set('period', 'custom');
            params.set('start', period.start || '');
            params.set('end', period.end || '');
        }

        const searchType = (payload.scopes || {}).search_type;

        if (searchType) {
            params.set('search_type', searchType);
        }

        return `${this.pageReportUrl}?${params.toString()}`;
    },

    rowNotes() {
        return Object.values(this.rowAnswer || {}).flatMap((payload) => payload.notes || []);
    },

    rowEmptyCopy() {
        const notes = this.rowNotes();

        return notes.length > 0 ? notes.join(" ") : this.strings.rowEmpty;
    },
});
